import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { patientFileHighlightsRoot } from '@/hooks/useDocumentHighlights';
import { clinicDocumentRegisterArgs, clinicDocumentStoragePath } from '@/lib/documentSource';
import { DocumentStorageAccessError } from '@/lib/documentStorageAccess';
import { storedDocumentDate } from '@/lib/patientFileLabel';
import { supabase } from '@/lib/supabase';
import { documentStorageUploadOptions, resolveFileMimeType } from '@/utils/fileMime';

// ── Types ─────────────────────────────────────────────────────────────────────
// Manual types until database.generated.ts is regenerated with MOD-11 tables.

export interface DocumentRow {
  id: string;
  clinic_id: string;
  patient_id: string | null;
  referral_id: string | null;
  referral_category: string | null;
  referral_field_name: string | null;
  category_id: string | null;
  filename: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  storage_path: string;
  tags: string[];
  notes: string | null;
  uploaded_by: string | null;
  archived_at: string | null;
  archived_by: string | null;
  /** Letter/scan date (Dokumentdato). Null when the file has no document date. */
  document_date: string | null;
  /** Clinical name from Dokument info (Dokumentnavn). The Filer list shows this when set. */
  document_title: string | null;
  /** Content author from Dokument info. Not uploaded_by. */
  document_author: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentListItem extends DocumentRow {
  patient_first_name: string | null;
  patient_last_name: string | null;
  uploader_name: string | null;
}

export interface DocumentListFilters {
  search?: string;
  patientId?: string | null;
  tags?: string[];
  showArchived?: boolean;
  page?: number;
}

export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
] as const;

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
const PAGE_SIZE = 50;

// ── Query keys ────────────────────────────────────────────────────────────────

export const documentsListKey = (
  clinicId: string | null | undefined,
  filters: DocumentListFilters,
) => ['documents', 'list', clinicId ?? null, filters] as const;

export const patientDocumentsKey = (
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) => ['documents', 'patient', clinicId ?? null, patientId ?? null] as const;

export const referralDocumentsKey = (
  referralId: string | null | undefined,
  category?: string | null,
  fieldName?: string | null,
) => ['documents', 'referral', referralId ?? null, category ?? null, fieldName ?? null] as const;

// ── Queries ───────────────────────────────────────────────────────────────────

export function useDocumentsList(
  clinicId: string | null | undefined,
  filters: DocumentListFilters = {},
) {
  const page = filters.page ?? 0;
  return useQuery({
    queryKey: documentsListKey(clinicId, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<DocumentListItem[]> => {
      if (!clinicId) return [];

      let q = supabase
        .from('documents')
        .select(
          `id, clinic_id, patient_id, referral_id, referral_category, referral_field_name, category_id,
           filename, original_filename, mime_type, size_bytes, storage_path, tags, notes, uploaded_by,
           archived_at, archived_by, created_at, updated_at,
           patient:patients!documents_patient_id_fkey(first_name, last_name),
           uploader:users!documents_uploaded_by_fkey(name)`,
        )
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (!filters.showArchived) {
        q = q.is('archived_at', null);
      }

      if (filters.patientId) {
        q = q.eq('patient_id', filters.patientId);
      }

      if (filters.search?.trim()) {
        q = q.ilike('filename', `%${filters.search.trim()}%`);
      }

      if (filters.tags && filters.tags.length > 0) {
        q = q.overlaps('tags', filters.tags);
      }

      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row) => {
        const r = row as unknown as {
          patient: { first_name: string; last_name: string } | null;
          uploader: { name: string | null } | null;
        } & typeof row;
        return {
          id: row.id,
          clinic_id: row.clinic_id,
          patient_id: row.patient_id,
          referral_id: row.referral_id,
          referral_category: row.referral_category,
          referral_field_name: row.referral_field_name,
          category_id: row.category_id,
          filename: row.filename,
          original_filename: row.original_filename,
          mime_type: row.mime_type,
          size_bytes: row.size_bytes,
          storage_path: row.storage_path,
          tags: row.tags ?? [],
          notes: row.notes,
          uploaded_by: row.uploaded_by,
          archived_at: row.archived_at,
          archived_by: row.archived_by,
          document_date:
            (row as { document_date?: string }).document_date ?? row.created_at.slice(0, 10),
          // Omitted from this select on purpose: a missing migration must not
          // fail the clinic-wide file list. Patient file rows use select('*').
          document_title: null,
          document_author: null,
          created_at: row.created_at,
          updated_at: row.updated_at,
          patient_first_name: r.patient?.first_name ?? null,
          patient_last_name: r.patient?.last_name ?? null,
          uploader_name: r.uploader?.name ?? null,
        } satisfies DocumentListItem;
      });
    },
    staleTime: 15_000,
  });
}

export function usePatientDocuments(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
  showArchived = false,
) {
  return useQuery({
    queryKey: patientDocumentsKey(clinicId, patientId),
    enabled: Boolean(clinicId) && Boolean(patientId),
    queryFn: async (): Promise<DocumentRow[]> => {
      if (!clinicId || !patientId) return [];
      let q = supabase
        .from('documents')
        .select('*')
        .eq('clinic_id', clinicId)
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });

      if (!showArchived) {
        q = q.is('archived_at', null);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((row) => withDocumentInfo(row as DocumentRow));
    },
    staleTime: 10_000,
  });
}

export function useReferralDocuments(
  referralId: string | null | undefined,
  category?: string | null,
  fieldName?: string | null,
) {
  return useQuery({
    queryKey: referralDocumentsKey(referralId, category, fieldName),
    enabled: Boolean(referralId),
    queryFn: async (): Promise<DocumentRow[]> => {
      if (!referralId) return [];
      let q = supabase
        .from('documents')
        .select('*')
        .eq('referral_id', referralId)
        .is('archived_at', null)
        .order('created_at', { ascending: false });

      if (category) {
        q = q.eq('referral_category', category);
      }

      if (fieldName) {
        q = q.eq('referral_field_name', fieldName);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((row) => withDocumentInfo(row as DocumentRow));
    },
    staleTime: 10_000,
  });
}

function withDocumentInfo(row: DocumentRow): DocumentRow {
  return {
    ...row,
    document_date: storedDocumentDate(row.document_date),
    document_title: row.document_title ?? null,
    document_author: row.document_author ?? null,
  };
}

// ── File validation ───────────────────────────────────────────────────────────

export function validateDocumentFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    return 'size_limit_exceeded';
  }
  const mimeType = resolveFileMimeType(file);
  if (!ALLOWED_MIME_TYPES.includes(mimeType as (typeof ALLOWED_MIME_TYPES)[number])) {
    return 'invalid_type';
  }
  return null;
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export interface UploadDocumentParams {
  clinicId: string;
  patientId: string | null;
  file: File;
  tags?: string[];
  notes?: string | null;
  onProgress?: (percent: number) => void;
  referralId?: string | null;
  referralCategory?: string | null;
  referralFieldName?: string | null;
}

export function useUploadDocument() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clinicId,
      patientId,
      file,
      tags = [],
      notes = null,
      onProgress,
      referralId = null,
      referralCategory = null,
      referralFieldName = null,
    }: UploadDocumentParams): Promise<DocumentRow> => {
      const validationError = validateDocumentFile(file);
      if (validationError) throw new Error(validationError);

      const mimeType = resolveFileMimeType(file);

      // Build storage path: {clinic_id}/{patient_id|referral_id|'clinic'}/{doc_uuid}/{filename}
      const docSegment = crypto.randomUUID();
      const safeFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const folderSegment = referralId ? `referrals/${referralId}` : (patientId ?? 'clinic');
      const storagePath = `${clinicId}/${folderSegment}/${docSegment}/${safeFilename}`;

      onProgress?.(10);

      const { error: uploadError } = await supabase.storage
        .from('patient-documents')
        .upload(storagePath, file, documentStorageUploadOptions(file));

      if (uploadError) throw uploadError;

      onProgress?.(70);

      // Insert document metadata with referral tracking
      const { data: docData, error: insertError } = await supabase
        .from('documents')
        .insert({
          clinic_id: clinicId,
          patient_id: patientId,
          referral_id: referralId,
          referral_category: referralCategory,
          referral_field_name: referralFieldName,
          filename: safeFilename,
          original_filename: file.name,
          mime_type: mimeType,
          size_bytes: file.size,
          storage_path: storagePath,
          tags: tags,
          notes: notes,
        })
        .select()
        .single();

      if (insertError) {
        // Storage upload succeeded but metadata failed — storage object is orphaned.
        throw insertError;
      }

      onProgress?.(100);

      return docData as DocumentRow;
    },
    onSuccess: (_data, { clinicId, patientId, referralId }) => {
      void qc.invalidateQueries({ queryKey: ['documents', 'list', clinicId] });
      if (patientId) {
        void qc.invalidateQueries({ queryKey: patientDocumentsKey(clinicId, patientId) });
      }
      if (referralId) {
        void qc.invalidateQueries({ queryKey: ['documents', 'referral', referralId] });
      }
    },
  });
}

export interface UploadClinicPatientDocumentParams {
  clinicId: string;
  patientId: string;
  file: File;
  notes?: string | null;
  categoryId?: string | null;
  documentDate?: string | null;
  onProgress?: (percent: number) => void;
}

export function useUploadClinicPatientDocument() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clinicId,
      patientId,
      file,
      notes = null,
      categoryId = null,
      documentDate = null,
      onProgress,
    }: UploadClinicPatientDocumentParams): Promise<DocumentRow> => {
      const validationError = validateDocumentFile(file);
      if (validationError) throw new Error(validationError);

      const mimeType = resolveFileMimeType(file);
      const docSegment = crypto.randomUUID();
      const safeFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = clinicDocumentStoragePath({
        clinicId,
        patientId,
        documentSegment: docSegment,
        safeFilename,
      });

      onProgress?.(10);

      const { error: uploadError } = await supabase.storage
        .from('patient-documents')
        .upload(storagePath, file, documentStorageUploadOptions(file));

      if (uploadError) throw uploadError;

      onProgress?.(70);

      const { data, error } = await (supabase.rpc as Function)('register_clinic_patient_document', {
        ...clinicDocumentRegisterArgs({
          clinicId,
          patientId,
          filename: safeFilename,
          originalFilename: file.name,
          mimeType,
          sizeBytes: file.size,
          storagePath,
          categoryId,
          documentDate,
        }),
        p_notes: notes ?? undefined,
      });

      if (error || !data) {
        await supabase.storage.from('patient-documents').remove([storagePath]);
        throw error ?? new Error('upload_failed');
      }

      onProgress?.(100);
      return data;
    },
    onSuccess: async (doc, { clinicId, patientId }) => {
      const key = patientDocumentsKey(clinicId, patientId);
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<DocumentRow[]>(key, (current) => {
        const row: DocumentRow = {
          ...doc,
          tags: doc.tags ?? [],
          document_date: storedDocumentDate(doc.document_date),
          document_title: doc.document_title ?? null,
          document_author: doc.document_author ?? null,
        };
        const rows = current ?? [];
        if (rows.some((item) => item.id === row.id)) {
          return rows.map((item) => (item.id === row.id ? row : item));
        }
        return [row, ...rows];
      });
      void qc.invalidateQueries({ queryKey: ['documents', 'list', clinicId] });
    },
  });
}

export function useRegisterDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      clinic_id: string;
      patient_id: string | null;
      filename: string;
      original_filename: string;
      mime_type: string;
      size_bytes: number;
      storage_path: string;
      tags?: string[];
      notes?: string | null;
    }): Promise<DocumentRow> => {
      const { data, error } = await supabase.rpc('register_document', {
        p_clinic_id: params.clinic_id,
        p_patient_id: params.patient_id as string, // RPC requires string; clinic-level docs pass empty string
        p_filename: params.filename,
        p_original_filename: params.original_filename,
        p_mime_type: params.mime_type,
        p_size_bytes: params.size_bytes,
        p_storage_path: params.storage_path,
        p_tags: params.tags ?? [],
        p_notes: params.notes ?? undefined,
      });
      if (error) throw error;
      return data as unknown as DocumentRow;
    },
    onSuccess: (_data, params) => {
      void qc.invalidateQueries({ queryKey: ['documents', 'list', params.clinic_id] });
      if (params.patient_id) {
        void qc.invalidateQueries({
          queryKey: patientDocumentsKey(params.clinic_id, params.patient_id),
        });
      }
    },
  });
}

export type DocumentDownloadUrlInput = string | { documentId: string; filename?: string | null };

export function useGetDocumentDownloadUrl() {
  return useMutation({
    mutationFn: async (input: DocumentDownloadUrlInput): Promise<string> => {
      const documentId = typeof input === 'string' ? input : input.documentId;
      const filename = typeof input === 'string' ? null : input.filename?.trim() || null;
      // Call RPC to validate auth + insert audit row.
      const { data, error } = await supabase.rpc('get_document_download_url', {
        p_document_id: documentId,
      });
      if (error) throw error;

      const result = data as unknown as { storage_path: string; expires_at: string };

      // `download` asks Storage for Content-Disposition: attachment and the original name.
      const { data: signedData, error: signedError } = await supabase.storage
        .from('patient-documents')
        .createSignedUrl(result.storage_path, 60, filename ? { download: filename } : undefined);

      if (signedError || !signedData?.signedUrl) throw new DocumentStorageAccessError();

      return signedData.signedUrl;
    },
  });
}

export function useUpdateDocumentMetadata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      document_id: string;
      tags?: string[];
      notes?: string | null;
    }): Promise<DocumentRow> => {
      const { data, error } = await supabase.rpc('update_document_metadata', {
        p_document_id: params.document_id,
        p_tags: params.tags ?? undefined,
        p_notes: params.notes ?? undefined,
      });
      if (error) throw error;
      return data as unknown as DocumentRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useAssignReferralDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      documentId: string;
      referralCategory: string | null;
      referralFieldName: string | null;
    }): Promise<DocumentRow> => {
      // RPC is new; not yet in database.generated.ts — cast until types are regenerated.
      const { data, error } = await (supabase.rpc as Function)('assign_referral_document', {
        p_document_id: params.documentId,
        p_referral_category: params.referralCategory,
        p_referral_field_name: params.referralFieldName,
      });
      if (error) throw error;
      return data as unknown as DocumentRow;
    },
    onSuccess: (doc) => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      if (doc.referral_id) {
        void qc.invalidateQueries({ queryKey: ['documents', 'referral', doc.referral_id] });
      }
    },
  });
}

export function useUpdatePatientDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      documentId: string;
      originalFilename?: string;
      documentDate?: string;
    }): Promise<DocumentRow> => {
      const { data, error } = await (supabase.rpc as Function)('update_patient_document', {
        p_document_id: params.documentId,
        p_original_filename: params.originalFilename ?? undefined,
        p_document_date: params.documentDate ?? undefined,
      });
      if (error) throw error;
      return data as unknown as DocumentRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: patientFileHighlightsRoot });
    },
  });
}

export function useDeletePatientDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string): Promise<DocumentRow> => {
      const { data, error } = await (supabase.rpc as Function)('delete_patient_document', {
        p_document_id: documentId,
      });
      if (error) throw error;
      return data as unknown as DocumentRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useArchiveDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string): Promise<DocumentRow> => {
      const { data, error } = await supabase.rpc('archive_document', {
        p_document_id: documentId,
      });
      if (error) throw error;
      return data as unknown as DocumentRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useRestoreDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string): Promise<DocumentRow> => {
      const { data, error } = await supabase.rpc('restore_document', {
        p_document_id: documentId,
      });
      if (error) throw error;
      return data as unknown as DocumentRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}
